/**
 * @fileoverview Student profile page ("Studio Quiet") — identity, musical
 * profile (favorite artist + interests, editable), connected instructors,
 * notification preferences, resources, and account actions.
 */

import { useState, useEffect, useContext } from "react";
import { StudentAuthContext } from "@/App";
import { toast } from "sonner";
import studentApi from "@/lib/studentApi";
import { calendarSyncFlags } from "@/lib/featureFlags";
import CalendarSyncSection from "@/components/settings/CalendarSyncSection";
import { getErrorMessage } from "@/lib/api";
import { displayName } from "@/lib/userName";
import { cn } from "@/lib/utils";
import { Card, Icon, Button, Avatar, Eyebrow, Tag, ListRow } from "@/components/studio/primitives";
import { SHeader, SToggle } from "@/components/studio/studentUi";
import PracticeRemindersCard from "@/components/PracticeRemindersCard";
import ThemeToggle from "@/components/ThemeToggle";
import { supportEmail } from "@/lib/companyBrand";
import { INTEREST_OPTIONS } from "@/lib/interests";

const NOTIF_ROWS = [
  { key: "new_lesson_summary", label: "New lesson summary", sub: "When your teacher adds a new lesson" },
  { key: "portfolio_ready", label: "Portfolio ready", sub: "When you have enough lessons to create your portfolio" },
  { key: "portfolio_updated", label: "Portfolio updated", sub: "When your portfolio is updated with new lessons" },
];

const RESOURCES = [
  { icon: "book", label: "Hans Academy", sub: "Guides, tips, and how-to articles", href: "/student-guide", ext: true },
  { icon: "shield", label: "Security", href: "/security", ext: true },
  { icon: "eye", label: "Data privacy", href: "/privacy", ext: true },
  { icon: "message", label: "Contact support", sub: supportEmail(), href: `mailto:${supportEmail()}`, ext: true },
  { icon: "info", label: "Version 1.0.0" },
];

const StudentProfilePage = () => {
  const { student, logout } = useContext(StudentAuthContext) || {};
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [favoriteArtist, setFavoriteArtist] = useState("");
  const [interests, setInterests] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({
    new_lesson_summary: true,
    portfolio_ready: true,
    portfolio_updated: true,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await studentApi.get("/student/profile");
        setProfile(res.data);
        setFavoriteArtist(res.data.favorite_artist || "");
        setInterests(res.data.interests || []);
        if (res.data.notification_preferences) setNotifPrefs(res.data.notification_preferences);
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const toggleInterest = (interest) => {
    setInterests((prev) => (prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await studentApi.put("/student/interests", { interests, favorite_artist: favoriteArtist.trim() || null });
      setProfile({ ...profile, favorite_artist: favoriteArtist.trim() || null, interests });
      setEditingProfile(false);
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDisconnect = async (teacherUserId) => {
    setDisconnecting(true);
    try {
      await studentApi.post(`/student/disconnect/${teacherUserId}`);
      toast.success("Disconnected from instructor");
      setProfile({ ...profile, teachers: profile.teachers.filter((t) => t.user_id !== teacherUserId) });
      setConfirmDisconnect(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to disconnect"));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await studentApi.delete("/student/auth/account");
      logout();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete account"));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const toggleNotif = async (key) => {
    const prev = notifPrefs;
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    try {
      await studentApi.put("/student/notification-preferences", updated);
    } catch (_e) {
      setNotifPrefs(prev);
      toast.error("Couldn't save notification preference");
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[900px] px-6 pb-32 pt-11 md:px-12">
        <div className="space-y-4">
          <div className="skeleton h-10 w-1/2 rounded-xl" />
          <div className="skeleton h-64 rounded-[20px]" />
          <div className="skeleton h-40 rounded-[20px]" />
        </div>
      </div>
    );
  }

  const name = profile?.name || displayName(student) || "Student";

  return (
    <div className="mx-auto max-w-[900px] px-6 pb-32 pt-11 md:px-12" data-testid="student-profile">
      <SHeader eyebrow="Account" title="Your profile" />

      {/* Identity + musical profile */}
      <Card className="mb-[22px]">
        <div className="flex items-center gap-[18px]">
          <div className="flex h-[66px] w-[66px] shrink-0 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-interactive)]">
            <Icon name="user" size={28} strokeWidth={1.6} className="text-[var(--text-tertiary)]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-sans text-[22px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{name}</div>
            <div className="mt-0.5 truncate font-mono text-[13px] text-[var(--text-tertiary)]">{profile?.email}</div>
          </div>
        </div>

        <div className="my-6 h-px bg-[var(--studio-divider)]" />

        {/* Instruments */}
        {profile?.instruments?.length > 0 && (
          <>
            <Eyebrow>Instruments</Eyebrow>
            <div className="mt-3 flex flex-wrap gap-2">
              {profile.instruments.map((inst) => (
                <Tag key={inst} accent>
                  <Icon name="music" size={12} className="text-[var(--accent-yellow-text)]" /> {inst}
                </Tag>
              ))}
            </div>
          </>
        )}

        {/* Musical profile */}
        <div className="mt-6 flex items-center justify-between">
          <Eyebrow>Musical profile</Eyebrow>
          {!editingProfile ? (
            <button
              type="button"
              onClick={() => setEditingProfile(true)}
              className="flex items-center gap-1.5 font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)]"
            >
              <Icon name="edit" size={13} /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="flex items-center gap-1.5 font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)] disabled:opacity-50"
              >
                <Icon name={savingProfile ? "loader" : "check"} size={14} className={savingProfile ? "animate-spin" : ""} /> Save
              </button>
              <button
                type="button"
                onClick={() => { setEditingProfile(false); setFavoriteArtist(profile?.favorite_artist || ""); setInterests(profile?.interests || []); }}
                className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                aria-label="Cancel editing"
              >
                <Icon name="x" size={16} />
              </button>
            </div>
          )}
        </div>

        {editingProfile ? (
          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                <Icon name="star" size={13} /> Favorite artist
              </label>
              <input
                value={favoriteArtist}
                onChange={(e) => setFavoriteArtist(e.target.value)}
                placeholder="e.g. Chopin, Miles Davis, Taylor Swift…"
                className="w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] px-3.5 py-2.5 font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow-border-strong)]"
              />
            </div>
            <div>
              <label className="mb-2 flex items-center gap-1.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                <Icon name="music" size={13} /> Musical interests
              </label>
              <div className="flex flex-wrap gap-2">
                {INTEREST_OPTIONS.map((interest) => {
                  const selected = interests.includes(interest);
                  return (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => toggleInterest(interest)}
                      className={cn(
                        "rounded-full border px-3 py-1.5 font-sans text-[13px] font-medium transition-colors",
                        selected
                          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                          : "border-[var(--studio-divider)] bg-[var(--studio-interactive)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                      )}
                    >
                      {interest}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-3.5 space-y-3">
            {profile?.favorite_artist && (
              <div className="flex items-center gap-2.5">
                <Icon name="star" size={16} strokeWidth={1.6} className="shrink-0 text-[var(--text-tertiary)]" />
                <span className="font-sans text-base font-medium text-[var(--text-primary)]">{profile.favorite_artist}</span>
              </div>
            )}
            {profile?.interests?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {profile.interests.map((interest) => <Tag key={interest}>{interest}</Tag>)}
              </div>
            )}
            {!profile?.favorite_artist && !profile?.interests?.length && (
              <p className="font-sans text-[15px] text-[var(--text-quaternary)]">Not set yet — tap Edit to add your musical interests.</p>
            )}
          </div>
        )}

        {/* Instructors */}
        {profile?.teachers?.length > 0 && (
          <div className="mt-6">
            <Eyebrow>Instructors</Eyebrow>
            <div className="mt-3.5 space-y-2">
              {profile.teachers.map((teacher) => (
                <div
                  key={teacher.user_id}
                  className="flex items-center gap-3.5 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-base)] px-4 py-3.5"
                >
                  {teacher.picture ? (
                    <img src={teacher.picture} alt="" className="h-10 w-10 rounded-full" />
                  ) : (
                    <Avatar name={teacher.name} size={40} accent />
                  )}
                  <span className="flex-1 font-sans text-base font-medium text-[var(--text-primary)]">{teacher.name}</span>

                  {confirmDisconnect === teacher.user_id ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDisconnect(teacher.user_id)}
                        disabled={disconnecting}
                        className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-dim)] px-3 py-1.5 font-sans text-[13px] font-medium text-[var(--state-danger)] transition-colors hover:bg-[var(--state-danger-hover)]"
                      >
                        {disconnecting ? "Leaving…" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDisconnect(null)}
                        className="px-2 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--state-success-border)] bg-[var(--state-success-dim)] px-3 py-1 font-sans text-[12px] font-semibold text-[var(--state-success)]">
                        <Icon name="graduation" size={13} strokeWidth={1.9} /> Connected
                      </span>
                      <button
                        type="button"
                        onClick={() => setConfirmDisconnect(teacher.user_id)}
                        aria-label="Leave instructor"
                        className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--state-danger-dim)] hover:text-[var(--state-danger)]"
                      >
                        <Icon name="logout" size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <CalendarSyncSection
        portal="student"
        client={studentApi}
        prefix="/student"
        available={calendarSyncFlags(student)}
        className="mb-[22px]"
      />

      {/* Appearance — a preference of this browser rather than of the
          account, so it saves locally and applies at once (lib/theme.js). */}
      <Card className="mb-[22px]" data-testid="appearance-card">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <Eyebrow>Appearance</Eyebrow>
            <div className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
              Light is the default. Dark suits a dim room. Saved on this device.
            </div>
          </div>
          <ThemeToggle />
        </div>
      </Card>

      {/* Practice reminders (F5) — above Notifications, because it is the one
          the student actively schedules rather than one they merely permit. */}
      <PracticeRemindersCard />

      {/* Notifications */}
      <Card className="mb-[22px]">
        <Eyebrow icon="bell">Notifications</Eyebrow>
        <div className="mt-1.5">
          {NOTIF_ROWS.map((r, i) => (
            <div
              key={r.key}
              className={cn(
                "flex items-center gap-4 py-4",
                i < NOTIF_ROWS.length - 1 && "border-b border-[var(--studio-divider)]",
              )}
            >
              <div className="flex-1">
                <div className="font-sans text-base font-medium text-[var(--text-primary)]">{r.label}</div>
                <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{r.sub}</div>
              </div>
              <SToggle on={notifPrefs[r.key]} onToggle={() => toggleNotif(r.key)} ariaLabel={r.label} />
            </div>
          ))}
        </div>
      </Card>

      {/* Resources */}
      <Eyebrow className="mb-3.5">Resources</Eyebrow>
      <Card padding={6} className="mb-[22px]">
        {RESOURCES.map((r, i, arr) => {
          const content = (
            <ListRow
              leading={
                <div className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] bg-[var(--studio-interactive)]">
                  <Icon name={r.icon} size={16} className="text-[var(--text-secondary)]" />
                </div>
              }
              title={r.label}
              subtitle={r.sub}
              trailing={r.ext ? <Icon name="external" size={14} className="text-[var(--text-tertiary)]" /> : null}
              borderBottom={i < arr.length - 1}
            />
          );
          return r.href ? (
            <a
              key={r.label}
              href={r.href}
              target={r.href.startsWith("mailto:") ? undefined : "_blank"}
              rel="noopener noreferrer"
              className="block"
            >
              {content}
            </a>
          ) : (
            <div key={r.label}>{content}</div>
          );
        })}
      </Card>

      {/* Account actions */}
      <Button variant="danger" icon="logout" fullWidth onPress={() => logout?.()} data-testid="student-logout">
        Sign out
      </Button>

      <div className="mt-4">
        {!confirmDelete ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="flex w-full items-center justify-center gap-2 py-2 font-sans text-[13px] text-[var(--text-quaternary)] transition-colors hover:text-[var(--text-tertiary)]"
          >
            <Icon name="trash" size={13} /> Delete account
          </button>
        ) : (
          <Card className="space-y-3">
            <p className="font-sans text-[13px] text-[var(--text-tertiary)]">
              This will permanently delete your account and all practice data. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-dim)] px-4 py-1.5 font-sans text-[13px] font-medium text-[var(--state-danger)] transition-colors hover:bg-[var(--state-danger-hover)] disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Permanently delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="px-4 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                Cancel
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StudentProfilePage;
